import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { socialStatus, socialConnect, socialDisconnect, socialGenerate, socialGenerateStatus, socialActivity, socialSelectPage, socialRefreshPages, socialDebugLive, socialAddPageById, socialBatchPlan } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FacebookLogo, InstagramLogo, Sparkle, PlugsConnected, ShieldCheck, CheckCircle, ArrowRight, ClockCounterClockwise, Plus, ImageSquare, SealCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { ImageSourcePicker } from "@/components/social/ImageSourcePicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const POST_TYPES = [
  ["facebook_post", "Facebook Post"], ["instagram_caption", "Instagram Caption"],
  ["product_launch", "Product Launch"], ["holiday", "Holiday Post"], ["sale", "Sale Post"],
  ["bundle", "Bundle Post"], ["educational", "Educational"], ["engagement", "Engagement"],
  ["brand_story", "Brand Story"],
];

function ConnectPanel({ status, brandName }) {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["social-status", activeId] });
  const connect = useMutation({
    mutationFn: () => socialConnect({ client_id: activeId, brand_name: brandName }),
    onSuccess: () => { inv(); toast.success("Test mode enabled"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Connect failed"),
  });
  const disconnect = useMutation({ mutationFn: () => socialDisconnect(activeId), onSuccess: () => { inv(); toast.success("Disconnected"); } });
  const selectPage = useMutation({
    mutationFn: (pageId) => socialSelectPage(activeId, pageId),
    onSuccess: (r) => { inv(); toast.success(`Active Page set: ${r.page_name}${r.selected_ig ? " (+ Instagram)" : ""}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not select page"),
  });
  const refreshPages = useMutation({
    mutationFn: () => socialRefreshPages(activeId),
    onSuccess: (r) => { inv(); toast.success(`Refreshed — found ${r.count} Page(s) from Meta`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not refresh pages"),
  });
  const [pageIdInput, setPageIdInput] = useState("");
  const addById = useMutation({
    mutationFn: () => socialAddPageById(activeId, pageIdInput.trim()),
    onSuccess: (r) => { inv(); setPageIdInput(""); toast.success(`Connected: ${r.page.name}${r.page.has_instagram ? ` (+ @${r.page.instagram_username || "instagram"})` : ""}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not connect that Page ID", { duration: 9000 }),
  });
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiag = async () => {
    setDiagLoading(true); setDiag(null);
    try { setDiag(await socialDebugLive(activeId)); }
    catch (e) { setDiag({ error: e?.response?.data?.detail || "Diagnostics failed" }); }
    finally { setDiagLoading(false); }
  };
  const copyDiag = () => { navigator.clipboard?.writeText(JSON.stringify(diag, null, 2)); toast.success("Diagnostics copied — paste it to support"); };
  const goLive = () => { window.location.href = `${BACKEND}/api/social/oauth/start?client_id=${activeId}`; };

  if (status?.connected) {
    const pages = status.pages || [];
    const selectedId = status.selected_page;
    const page = pages.find((p) => p.id === selectedId) || null;
    const isLive = !status.test_mode;
    return (
      <div className="card-matte p-5" data-testid="social-connection">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><PlugsConnected size={16} className="brand-text" weight="fill" />
            <span className="font-head font-semibold">Meta Connection</span></div>
          {status.test_mode
            ? <span data-testid="test-mode-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">TEST MODE</span>
            : <span data-testid="live-mode-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">● LIVE</span>}
        </div>

        {/* Active selection */}
        {page ? (
          <>
            <div className="text-sm text-[#cfcfcf] flex items-center gap-2"><FacebookLogo size={16} className="text-sky-400" /> <b>{page.name}</b> <span className="text-[10px] text-emerald-400">active</span></div>
            {page.instagram_business_account_id
              ? <div className="text-sm text-[#cfcfcf] flex items-center gap-2 mt-1"><InstagramLogo size={16} className="text-pink-400" /> Instagram Business linked{page.instagram_username ? ` — @${page.instagram_username}` : ""}</div>
              : (isLive && <div className="text-[11px] text-amber-300/80 mt-1 flex items-center gap-1"><InstagramLogo size={14} /> No Instagram Business account linked to this Page.</div>)}
          </>
        ) : (isLive && pages.length > 0 && (
          <div className="text-sm text-amber-300 flex items-center gap-2" data-testid="no-page-selected">⚠️ Choose which Page to use for this workspace:</div>
        ))}

        {/* Page picker — lists ALL authorized pages */}
        {isLive && pages.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="page-picker">
            <div className="label-caps">Authorized Pages ({pages.length}) — select the active one for this workspace</div>
            {pages.map((p) => (
              <button key={p.id} data-testid={`select-page-${p.id}`} onClick={() => selectPage.mutate(p.id)} disabled={selectPage.isPending}
                className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between transition-colors ${p.id === selectedId ? "border-emerald-500/40 bg-emerald-500/[0.08]" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                <span className="flex items-center gap-2 text-sm">
                  <FacebookLogo size={15} className="text-sky-400" /> {p.name}
                  {p.instagram_business_account_id && <InstagramLogo size={13} className="text-pink-400" title="Instagram linked" />}
                </span>
                {p.id === selectedId
                  ? <span className="text-[10px] font-semibold text-emerald-400 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Active</span>
                  : <span className="text-[10px] text-[#9a9a9a]">Use this</span>}
              </button>
            ))}
          </div>
        )}

        {status.test_mode
          ? <div className="text-[11px] text-amber-300/80 mt-3">Test mode simulates publishing (no live posts).{status.meta_configured ? " Click below to connect your real Facebook Page & go LIVE." : " Ask an admin to set META_APP_ID & META_APP_SECRET to enable live publishing."}</div>
          : <div className="text-[11px] text-emerald-300/80 mt-3">✓ Live — approved posts publish to the active Page{page?.instagram_business_account_id ? " & its Instagram" : ""}.</div>}
        <div className="flex items-center gap-3 mt-3">
          {status.meta_configured && status.test_mode && (
            <Button data-testid="social-go-live-btn" onClick={goLive} className="bg-[#1877F2] hover:bg-[#1877F2]/85 text-white h-8">
              <FacebookLogo size={16} weight="fill" className="mr-1.5" /> Connect with Facebook (go live)
            </Button>
          )}
          {isLive && (
            <Button data-testid="social-refresh-pages-btn" variant="ghost" size="sm" onClick={() => refreshPages.mutate()} disabled={refreshPages.isPending} className="text-[#9a9a9a] hover:text-white px-0">{refreshPages.isPending ? "Refreshing…" : "Refresh pages from Meta"}</Button>
          )}
          {isLive && (
            <Button data-testid="social-reconnect-btn" variant="ghost" size="sm" onClick={goLive} className="text-[#9a9a9a] hover:text-white px-0">Reconnect / refresh pages</Button>
          )}
          <Button data-testid="social-disconnect" variant="ghost" size="sm" onClick={() => disconnect.mutate()} className="text-[#9a9a9a] hover:text-red-400 px-0">Disconnect</Button>
        </div>

        {isLive && (
          <div className="mt-3 pt-3 border-t border-white/8">
            <button data-testid="social-run-diagnostics" onClick={runDiag} disabled={diagLoading}
              className="text-[11px] text-[#7a7a7a] hover:text-white underline underline-offset-2">
              {diagLoading ? "Running connection diagnostics…" : "Run connection diagnostics (why is a Page missing?)"}
            </button>
            {diag && (
              <div className="mt-2" data-testid="social-diagnostics-output">
                <div className="flex items-center justify-between mb-1">
                  <span className="label-caps">Raw Meta discovery</span>
                  <button data-testid="social-copy-diagnostics" onClick={copyDiag} className="text-[11px] brand-text hover:text-[#A56CFF]">Copy JSON</button>
                </div>
                <pre className="text-[10px] leading-relaxed text-[#cfcfcf] bg-[#10101A] border border-white/10 rounded-lg p-3 max-h-72 overflow-auto whitespace-pre-wrap break-all">{JSON.stringify(diag, null, 2)}</pre>
              </div>
            )}

            {/* Escape hatch: connect a Page by its ID when Meta won't enumerate it */}
            <div className="mt-3">
              <div className="label-caps mb-1">Page missing? Connect it by ID</div>
              <p className="text-[11px] text-[#7a7a7a] mb-2">If a Page you manage never appears above (common for Pages under a Business Portfolio), paste its numeric Facebook Page ID — Vozelto will attach it directly.</p>
              <div className="flex items-center gap-2">
                <Input data-testid="add-page-id-input" value={pageIdInput} onChange={(e) => setPageIdInput(e.target.value)}
                  placeholder="e.g. 1234567890" className="bg-[#10101A] border-white/10 h-8 text-sm" />
                <Button data-testid="add-page-id-btn" size="sm" onClick={() => addById.mutate()} disabled={addById.isPending || !pageIdInput.trim()}
                  className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-8 shrink-0"><Plus size={14} className="mr-1" />{addById.isPending ? "Connecting…" : "Connect Page"}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Not connected yet
  return (
    <div className="card-matte p-5" data-testid="social-connect-panel">
      <div className="flex items-center gap-2 mb-1"><PlugsConnected size={16} className="brand-text" weight="fill" />
        <span className="font-head font-semibold">Connect Meta (Facebook + Instagram)</span></div>
      {status?.meta_configured ? (
        <>
          <p className="text-sm text-[#9a9a9a] mb-4">Connect this workspace's Facebook Page & Instagram Business account to publish approved posts for real. You'll be redirected to Facebook to authorize.</p>
          <Button data-testid="social-connect-live-btn" onClick={goLive} className="bg-[#1877F2] hover:bg-[#1877F2]/85 text-white">
            <FacebookLogo size={18} weight="fill" className="mr-2" /> Connect with Facebook
          </Button>
          <div className="mt-4 pt-4 border-t border-white/8">
            <button data-testid="social-testmode-btn" onClick={() => connect.mutate()} disabled={connect.isPending} className="text-xs text-[#9a9a9a] hover:text-white underline underline-offset-2">
              {connect.isPending ? "Enabling…" : "Or try Test Mode (simulated publishing) →"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[#9a9a9a] mb-4">Live publishing requires the Vozelto Meta app credentials. An admin must set <code className="text-amber-300">META_APP_ID</code> and <code className="text-amber-300">META_APP_SECRET</code> in the deployment secrets (then redeploy). Until then you can use <span className="text-amber-300 font-semibold">Test Mode</span>.</p>
          <Button data-testid="social-connect-btn" onClick={() => connect.mutate()} disabled={connect.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            {connect.isPending ? "Connecting…" : "Connect in Test Mode"}
          </Button>
        </>
      )}
    </div>
  );
}

export default function AiSocialManagerPage() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState(["Facebook"]);
  const [postType, setPostType] = useState("facebook_post");
  const [topic, setTopic] = useState("");
  const [product, setProduct] = useState("");
  const [count, setCount] = useState(1);
  const [includeImage, setIncludeImage] = useState(true);
  const [brandAssetsOnly, setBrandAssetsOnly] = useState(false);
  const [imgSrc, setImgSrc] = useState({ image_source: "ai" });

  const statusQ = useQuery({ queryKey: ["social-status", activeId], queryFn: () => socialStatus(activeId), enabled: !!activeId });
  const activityQ = useQuery({ queryKey: ["social-activity", activeId], queryFn: () => socialActivity(activeId), enabled: !!activeId });
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get("meta_connected")) {
      toast.success("Facebook connected — live publishing is now enabled ✓");
      statusQ.refetch();
      setParams({}, { replace: true });
    } else if (params.get("meta_choose_page")) {
      toast.info("Connected! Multiple Pages were authorized — choose the active Page for this workspace below.", { duration: 9000 });
      statusQ.refetch();
      setParams({}, { replace: true });
    } else if (params.get("meta_error")) {
      toast.error(`Meta connection failed: ${params.get("meta_error")}`, { duration: 9000 });
      setParams({}, { replace: true });
    }
  }, [params]); // eslint-disable-line

  const gen = useMutation({
    mutationFn: async () => {
      // Start async job (returns instantly — no long-held connection = no 524)
      // For non-AI sources, include_image is implied by the selected image.
      const useImage = imgSrc.image_source === "ai" ? includeImage : true;
      const start = await socialGenerate({ client_id: activeId, platforms, post_type: postType, topic, product_title: product, count: Number(count), include_image: useImage, brand_assets_only: imgSrc.image_source === "ai" && brandAssetsOnly, image_source: imgSrc.image_source, selected_library_id: imgSrc.selected_library_id, selected_asset_id: imgSrc.selected_asset_id, enhance_uploaded: imgSrc.enhance_uploaded !== false });
      const jobId = start.job_id;
      if (!jobId) throw new Error("no job");
      // Poll until done/error (max ~180s), every 3s
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await socialGenerateStatus(jobId).catch(() => null);
        if (!s) continue;
        if (s.status === "done") return s;
        if (s.status === "error") throw new Error(s.error || "Generation failed");
      }
      throw new Error("timeout");
    },
    onSuccess: (r) => {
      toast.success(`${r.created} post(s) drafted → Approval Queue. Nothing publishes until you approve.`);
      if (r.image_failed > 0) {
        const strict = imgSrc.image_source === "ai" && brandAssetsOnly;
        if (strict) {
          toast.error(r.image_error || "Official product image required — upload or choose from Media Library.", { duration: 10000 });
          return;
        }
        const msg = imgSrc.image_source === "ai"
          ? `${r.image_failed} image(s) couldn't be generated — your AI image budget may be exhausted (Profile → Universal Key → Add Balance). The text posts are ready to review.`
          : `${r.image_failed} post(s) couldn't attach your selected image. The text posts are ready — try re-selecting the image.`;
        toast.warning(msg, { duration: 9000 });
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Generation failed"),
  });

  const togglePlatform = (p) => setPlatforms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  return (
    <PageContainer>
      <PageHeader eyebrow="AI Command" title="AI Social Media Manager"
        subtitle="Generate on-brand Facebook & Instagram posts from your workspace, products and calendar. Every post is approval-gated — nothing publishes until you approve it." />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ConnectPanel status={statusQ.data} brandName={activeClient?.name} />

          <div className="card-matte p-5" data-testid="social-generator">
            <div className="flex items-center gap-2 mb-4"><Sparkle size={16} className="brand-text" weight="fill" />
              <span className="font-head font-semibold">Generate posts</span></div>

            <Label className="label-caps">Platforms</Label>
            <div className="flex gap-2 mt-1 mb-4">
              <button data-testid="platform-facebook" onClick={() => togglePlatform("Facebook")}
                className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border ${platforms.includes("Facebook") ? "border-sky-500/50 bg-sky-500/10 text-white" : "border-white/10 text-[#9a9a9a]"}`}>
                <FacebookLogo size={16} /> Facebook</button>
              <button data-testid="platform-instagram" onClick={() => togglePlatform("Instagram")}
                className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border ${platforms.includes("Instagram") ? "border-pink-500/50 bg-pink-500/10 text-white" : "border-white/10 text-[#9a9a9a]"}`}>
                <InstagramLogo size={16} /> Instagram</button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="label-caps">Post type</Label>
                <select data-testid="post-type" value={postType} onChange={(e) => setPostType(e.target.value)} className="w-full bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1">
                  {POST_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select></div>
              <div><Label className="label-caps">How many (per platform)</Label>
                <Input data-testid="post-count" type="number" min={1} max={5} value={count} onChange={(e) => setCount(e.target.value)} className="bg-[#10101A] border-white/10 mt-1" /></div>
            </div>
            <div className="mt-4"><Label className="label-caps">Featured product (optional)</Label>
              <Input data-testid="post-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. Midnight Roast" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div className="mt-4"><Label className="label-caps">Topic / instructions (optional)</Label>
              <Textarea data-testid="post-topic" value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} placeholder="e.g. Promote our weekend flash sale with urgency" className="bg-[#10101A] border-white/10 mt-1" /></div>

            <ImageSourcePicker clientId={activeId} onChange={setImgSrc} />

            {imgSrc.image_source === "ai" && (
            <div className="mt-4 flex items-center justify-between rounded-lg border border-white/10 bg-[#10101A] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <ImageSquare size={16} className={includeImage ? "brand-text" : "text-[#7a7a7a]"} weight={includeImage ? "fill" : "regular"} />
                <div>
                  <div className="text-sm text-white">Generate matching image</div>
                  <div className="text-[11px] text-[#7a7a7a]">AI creates an on-brand image for each post (adds ~15–25s). Off = text-only.</div>
                </div>
              </div>
              <button data-testid="toggle-generate-image" onClick={() => setIncludeImage((v) => !v)} role="switch" aria-checked={includeImage}
                className={`relative h-6 w-11 rounded-full transition-colors ${includeImage ? "bg-[#6B3DFF]" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${includeImage ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            )}

            {imgSrc.image_source === "ai" && includeImage && (
            <label data-testid="brand-assets-only-row" className="mt-2 flex items-start gap-2.5 rounded-lg border border-white/10 bg-[#10101A] px-3 py-2.5 cursor-pointer">
              <input data-testid="toggle-brand-assets-only" type="checkbox" checked={brandAssetsOnly}
                onChange={(e) => setBrandAssetsOnly(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#6B3DFF]" />
              <div>
                <div className="text-sm text-white flex items-center gap-1.5"><SealCheck size={14} className={brandAssetsOnly ? "text-emerald-400" : "text-[#7a7a7a]"} weight={brandAssetsOnly ? "fill" : "regular"} /> Use Official Brand Assets Only</div>
                <div className="text-[11px] text-[#7a7a7a]">Images come exclusively from your product photos, Brand Assets or Media Library — the AI never invents packaging or logos. If no official image matches, the post waits instead of using a placeholder.</div>
              </div>
            </label>
            )}

            {imgSrc.image_source !== "ai" && !imgSrc.selected_library_id && !imgSrc.selected_asset_id && (
              <p className="mt-3 text-[11px] text-amber-300/80" data-testid="select-image-hint">Select or upload an image above to use for these posts.</p>
            )}

            {(() => {
              const needsPick = imgSrc.image_source !== "ai" && !imgSrc.selected_library_id && !imgSrc.selected_asset_id;
              return (
                <Button data-testid="social-generate-btn" onClick={() => gen.mutate()} disabled={gen.isPending || !platforms.length || needsPick}
                  className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white mt-4">
                  <Sparkle size={16} className="mr-1.5" weight="fill" /> {gen.isPending ? "Generating…" : "Generate → Approval Queue"}
                </Button>
              );
            })()}
            <div className="flex items-center gap-2 mt-3 text-xs text-[#7a7a7a]">
              <ShieldCheck size={14} className="brand-text" /> Drafts go to the Approval Queue. Approve, then Publish or Schedule.
              <button data-testid="goto-approvals" onClick={() => navigate("/approvals")} className="brand-text hover:text-[#A56CFF] flex items-center gap-1 ml-auto">Open Approval Queue <ArrowRight size={12} /></button>
            </div>
          </div>
        </div>

        <div className="card-matte p-5 h-fit" data-testid="social-activity">
          <div className="flex items-center gap-2 mb-3"><ClockCounterClockwise size={15} className="brand-text" />
            <span className="font-head font-semibold text-sm">Publish Activity</span></div>
          <div className="space-y-2">
            {(activityQ.data || []).slice(0, 12).map((a) => (
              <div key={a.id} className="text-xs border-b border-white/5 pb-2">
                <div className="flex items-center gap-1.5">
                  {a.platform === "Instagram" ? <InstagramLogo size={12} className="text-pink-400" /> : <FacebookLogo size={12} className="text-sky-400" />}
                  <span className="text-[#cfcfcf] truncate flex-1">{a.title}</span>
                  <span className={a.status === "failed" ? "text-red-400" : a.status === "scheduled" ? "text-sky-400" : "text-emerald-400"}>{a.status}</span>
                </div>
                {a.error ? <div className="text-[10px] text-red-400/80 mt-0.5">{a.error}</div>
                  : <div className="text-[10px] text-[#7a7a7a] mt-0.5">{a.test_mode ? "TEST · " : ""}{a.post_id}</div>}
              </div>
            ))}
            {!(activityQ.data || []).length && <div className="text-xs text-[#5a5a5a]">No posts published yet.</div>}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
